/* eslint-disable unicorn/no-null */
import type {
  CanvasRenderingTarget2D,
  BitmapCoordinatesRenderingScope,
} from 'fancy-canvas'
import type {
  ICustomSeriesPaneRenderer,
  PaneRendererCustomData,
  PriceToCoordinateConverter,
  IRange,
  Time,
  CustomSeriesOptions,
} from 'lightweight-charts'
import type { CandlestickData } from 'lightweight-charts'

interface BarItem {
  openY: number
  highY: number
  lowY: number
  closeY: number
  x: number
  isUp: boolean
}

interface BitmapPositionLength {
  /** coordinate for use with a bitmap rendering scope */
  position: number
  /** length for use with a bitmap rendering scope */
  length: number
}

export interface RoundedCandleSeriesOptions extends CustomSeriesOptions {
  upColor: string
  downColor: string
  radius: (barSpacing: number) => number
}

/**
 * Copied from lightweight-charts github repo
 * @see https://github.com/tradingview/lightweight-charts/tree/master/plugin-examples/src/plugins/rounded-candles-series
 */
export class RoundedCandleSeriesRenderer<TData extends CandlestickData>
  implements ICustomSeriesPaneRenderer
{
  _data: PaneRendererCustomData<Time, TData> | null = null
  _options: RoundedCandleSeriesOptions | null = null

  draw(
    target: CanvasRenderingTarget2D,
    priceConverter: PriceToCoordinateConverter,
  ): void {
    target.useBitmapCoordinateSpace((scope: BitmapCoordinatesRenderingScope) =>
      this.drawImpl(scope, priceConverter),
    )
  }

  update(
    data: PaneRendererCustomData<Time, TData>,
    options: RoundedCandleSeriesOptions,
  ): void {
    this._data = data
    this._options = options
  }

  drawImpl(
    renderingScope: BitmapCoordinatesRenderingScope,
    priceToCoordinate: PriceToCoordinateConverter,
  ): void {
    if (
      this._data === null ||
      this._data.bars.length === 0 ||
      this._data.visibleRange === null ||
      this._options === null
    ) {
      return
    }

    let lastClose = -Infinity

    const bars: Array<BarItem> = this._data.bars.map((bar) => {
      const { close, open, high, low } = bar.originalData
      const isUp = close >= lastClose

      lastClose = close

      const openY = priceToCoordinate(open) ?? 0
      const highY = priceToCoordinate(high) ?? 0
      const lowY = priceToCoordinate(low) ?? 0
      const closeY = priceToCoordinate(close) ?? 0

      return {
        openY,
        highY,
        lowY,
        closeY,
        x: bar.x,
        isUp,
      }
    })

    const radius = this._options.radius(this._data.barSpacing)
    this.drawCandles(renderingScope, bars, this._data.visibleRange, radius)
  }

  private drawCandles(
    renderingScope: BitmapCoordinatesRenderingScope,
    bars: ReadonlyArray<BarItem>,
    visibleRange: IRange<number>,
    radius: number,
  ): void {
    if (this._data === null || this._options === null) {
      return
    }

    const {
      context: ctx,
      horizontalPixelRatio,
      verticalPixelRatio,
    } = renderingScope

    // we want this in media width therefore using 1
    // positionsLine will adjust for pixelRatio
    const candleBodyWidth = this.optimalCandlestickWidth(
      this._data.barSpacing,
      1,
    )

    for (let i = visibleRange.from; i < visibleRange.to; i++) {
      const bar = bars[i]

      if (!bar) {
        continue
      }

      const verticalPositions = this.positionsBox(
        Math.min(bar.openY, bar.closeY),
        Math.max(bar.openY, bar.closeY),
        verticalPixelRatio,
      )
      const linePositions = this.positionsLine(
        bar.x,
        horizontalPixelRatio,
        candleBodyWidth,
      )

      ctx.fillStyle = bar.isUp ? this._options.upColor : this._options.downColor

      // roundRect might need to polyfilled for older browsers
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (ctx.roundRect) {
        ctx.beginPath()
        ctx.roundRect(
          linePositions.position,
          verticalPositions.position,
          linePositions.length,
          verticalPositions.length,
          radius,
        )
        ctx.fill()
      } else {
        ctx.fillRect(
          linePositions.position,
          verticalPositions.position,
          linePositions.length,
          verticalPositions.length,
        )
      }
    }
  }

  private optimalCandlestickWidth(
    barSpacing: number,
    pixelRatio: number,
  ): number {
    const barSpacingSpecialCaseFrom = 2.5
    const barSpacingSpecialCaseTo = 0
    const barSpacingSpecialCaseCoeff = 3

    if (
      barSpacing >= barSpacingSpecialCaseFrom &&
      barSpacing <= barSpacingSpecialCaseTo
    ) {
      return Math.floor(barSpacingSpecialCaseCoeff * pixelRatio)
    }
    // coeff should be 1 on small barspacing and go to 0.8 while groing bar spacing
    const barSpacingReducingCoeff = 0.2
    const coeff =
      1 -
      (barSpacingReducingCoeff *
        Math.atan(
          Math.max(barSpacingSpecialCaseTo, barSpacing) -
            barSpacingSpecialCaseTo,
        )) /
        (Math.PI * 0.5)
    const res = Math.floor(barSpacing * coeff * pixelRatio)
    const scaledBarSpacing = Math.floor(barSpacing * pixelRatio)
    const optimal = Math.min(res, scaledBarSpacing)

    return Math.max(Math.floor(pixelRatio), optimal)
  }

  /**
   * Calculates the bitmap position for an item with a desired length (height or width), and centred according to
   * an position coordinate defined in media sizing.
   * @see https://github.com/tradingview/lightweight-charts/blob/master/plugin-examples/src/helpers/dimensions/positions.ts#L15
   * @param positionMedia - position coordinate for the bar (in media coordinates)
   * @param pixelRatio - pixel ratio. Either horizontal for x positions, or vertical for y positions
   * @param desiredWidthMedia - desired width (in media coordinates)
   * @returns Position of of the start point and length dimension.
   */
  private positionsLine(
    positionMedia: number,
    pixelRatio: number,
    desiredWidthMedia: number = 1,
    widthIsBitmap?: boolean,
  ): BitmapPositionLength {
    const centreOffset = (lineBitmapWidth: number) =>
      Math.floor(lineBitmapWidth * 0.5)

    const scaledPosition = Math.round(pixelRatio * positionMedia) * 1
    const lineBitmapWidth = widthIsBitmap
      ? desiredWidthMedia
      : Math.round(desiredWidthMedia * pixelRatio)
    const offset = centreOffset(lineBitmapWidth)
    const position = scaledPosition - offset

    return { position, length: lineBitmapWidth }
  }

  /**
   * Determines the bitmap position and length for a dimension of a shape to be drawn.
   * @see https://github.com/tradingview/lightweight-charts/blob/master/plugin-examples/src/helpers/dimensions/positions.ts#L37
   * @param position1Media - media coordinate for the first point
   * @param position2Media - media coordinate for the second point
   * @param pixelRatio - pixel ratio for the corresponding axis (vertical or horizontal)
   * @returns Position of of the start point and length dimension.
   */
  private positionsBox(
    position1Media: number,
    position2Media: number,
    pixelRatio: number,
  ): BitmapPositionLength {
    const scaledPosition1 = Math.round(pixelRatio * position1Media)
    const scaledPosition2 = Math.round(pixelRatio * position2Media)

    return {
      position: Math.min(scaledPosition1, scaledPosition2),
      length: Math.abs(scaledPosition2 - scaledPosition1) + 1,
    }
  }
}
